import type {RouteRecordRaw} from 'vue-router'
import {markdownPageRoutes} from "@/entrypoints/www/markdownPageRoutes";
import {BLOG_CATEGORY_ROUTE_PATTERN} from "@/components/www/blog/shared/categories";
import {nordstromDemoPath} from '@/components/www/sell-on-nordstrom/nordstromLp'
import {SELL_ON_MARKETPLACES_PATH, marketplacesDemoPath} from '@/components/www/sell-on-marketplaces/sellOnMarketplacesLp'
import {AUDIT_QUERY_KEY, LISTING_REJECTIONS_PATH} from '@/components/www/listing-rejections/listingRejectionsLp'
import {
    MARKETPLACE_LISTING_ALTERNATIVES_PATH,
    alternativesDemoPath,
} from '@/components/www/marketplace-listing-alternatives/marketplaceListingAlternativesLp'
import {R2_DEMO_PATH, R2_PATH, R2_SOURCE} from '@/components/www/marketplace-listing-alternatives-r2/r2Lp'
import {COMPARE_PATH, findCompetitor} from '@/components/www/compare/compare'

// Route table shared by the client router (router.ts) and the SSR prerenderer
// (frontend/ssr/www.ts), which builds its own memory-history router from it.
// meta.headerTheme: 'dark' = light-on-dark header over a dark hero. Defaults to 'light'.
// meta.landingChrome: paid-landing chrome (logo + one CTA, legal footer only).
//
// The pre-footer CTA band is NOT global chrome: sections whose pages all end with it nest
// under PreFooterCtaLayout; standalone pages render it (or a section wrapper like
// pricing/PreFooterCta.vue) at the end of their own template. A route under that layout can
// override the band's copy with meta.preFooterCta.

// The tools pages end by naming the work the reader just watched a tool measure, so the band
// answers it directly instead of running the generic marketplace copy.
const TOOLS_PRE_FOOTER_CTA = {
    titleLines: ['Want to skip all of this?'],
    // PreFooterCta hard-breaks between lines and its subtitle measure is 38ch, so a line longer
    // than roughly 38 characters wraps again and the block reads as four ragged lines.
    subtitleLines: [
        'We fix this on every product you sell.',
        'Book 30 minutes and watch it work.',
    ],
    ctaLabel: 'Book a demo',
}

export const wwwRoutes: RouteRecordRaw[] = [
    {path: '/', component: () => import('@/components/www/home/Page.vue'), meta: {headerTheme: 'dark'}},
    {path: '/how-it-works', component: () => import('@/components/www/how-it-works/Page.vue')},
    {path: '/pricing', component: () => import('@/components/www/pricing/Page.vue')},
    {path: '/features', component: () => import('@/components/www/features/index/Page.vue')},
    {path: '/features/list-quickly', component: () => import('@/components/www/features/list-quickly/Page.vue')},
    {path: '/features/optimize-effortlessly', component: () => import('@/components/www/features/optimize-effortlessly/Page.vue')},
    {path: '/features/syndicate-seamlessly', component: () => import('@/components/www/features/syndicate-seamlessly/Page.vue')},
    {path: '/features/expand-limitlessly', component: () => import('@/components/www/features/expand-limitlessly/Page.vue')},
    {
        path: '/case-studies',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/case-studies/index/Page.vue')},
        ],
    },
    // Outside the CTA layout — the case-study pages end with their own dark CTA panel.
    {path: '/case-studies/universal-standard', component: () => import('@/components/www/case-studies/universal-standard/Page.vue')},
    {path: '/case-studies/furniq-uk', component: () => import('@/components/www/case-studies/furniq-uk/Page.vue')},
    {path: '/case-studies/noli-shop', component: () => import('@/components/www/case-studies/noli-shop/Page.vue')},
    {path: '/contact', component: () => import('@/components/www/contact/Page.vue')},
    // No pre-footer CTA — the about page ends with its own CTA panel.
    {path: '/about', component: () => import('@/components/www/about/Page.vue'), meta: {headerTheme: 'dark'}},
    {path: '/demo-request', component: () => import('@/components/www/demo-request/Page.vue')},
    {
        path: '/sell-on-nordstrom',
        component: () => import('@/components/www/sell-on-nordstrom/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'dark',
            landingCta: {label: 'Book intro call', to: nordstromDemoPath('header-demo')},
        },
    },
    {
        path: SELL_ON_MARKETPLACES_PATH,
        component: () => import('@/components/www/sell-on-marketplaces/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'dark',
            landingCta: {label: 'Book intro call', to: marketplacesDemoPath('header-demo')},
        },
    },
    {
        path: LISTING_REJECTIONS_PATH,
        component: () => import('@/components/www/listing-rejections/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'dark',
            // The page's only conversion action is the audit form; the header CTA opens it
            // via the query param the page watches (AUDIT_QUERY_KEY).
            landingCta: {label: 'Get my listing audit', to: `${LISTING_REJECTIONS_PATH}?${AUDIT_QUERY_KEY}=1`},
        },
    },
    {
        path: MARKETPLACE_LISTING_ALTERNATIVES_PATH,
        component: () => import('@/components/www/marketplace-listing-alternatives/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'dark',
            landingCta: {label: 'Book a 20-min demo', to: alternativesDemoPath('header-demo')},
        },
    },
    {
        path: R2_PATH,
        component: () => import('@/components/www/marketplace-listing-alternatives-r2/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'dark',
            landingHeaderSlim: true,
            landingCtaId: 'header-demo',
            landingCta: {
                label: 'Book a 20-min demo',
                to: `${R2_DEMO_PATH}?source=${R2_SOURCE}&variant=r2&cta=header-demo`,
            },
        },
    },
    {
        path: R2_DEMO_PATH,
        component: () => import('@/components/www/demo-request-r2/Page.vue'),
        meta: {
            landingChrome: true,
            headerTheme: 'light',
            landingHeaderSlim: true,
            landingLogoTo: R2_PATH,
            landingLogoLabel: 'Back to the landing page',
            landingCtaId: 'back',
            landingCta: {label: 'Back to page', to: R2_PATH},
        },
    },
    // Unknown /compare/:slug values land on the alternatives page. Rails 301s those
    // before the SPA loads; this beforeEnter covers in-app navigation.
    {
        path: `${COMPARE_PATH}/:slug`,
        component: () => import('@/components/www/compare/show/Page.vue'),
        props: route => ({slug: route.params.slug as string}),
        beforeEnter: to => (findCompetitor(to.params.slug as string) ? true : {path: MARKETPLACE_LISTING_ALTERNATIVES_PATH, query: to.query}),
    },
    {path: '/api-docs', component: () => import('@/components/www/api-docs/Page.vue')},
    {path: '/mcp', component: () => import('@/components/www/mcp/Page.vue'), meta: {headerTheme: 'dark'}},
    {path: '/terms-of-service', redirect: '/legal/terms-of-service'},
    {path: '/privacy-policy', redirect: '/legal/privacy-policy'},
    // Markdown pages (db/content/www/pages/<category>/<slug>.md), their per-category indexes,
    // and the internal /pages index — all generated from the files at build time.
    ...markdownPageRoutes,
    {
        path: '/integrations',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/integrations/index/Page.vue')},
            // Profiles are DB-backed (IntegrationProfile) — one dynamic route; unknown,
            // hidden, and coming-soon handles 404 on the API and render the not-found state.
            {
                path: ':handle',
                component: () => import('@/components/www/integrations/show/Page.vue'),
                props: route => ({handle: route.params.handle as string}),
            },
        ],
    },
    {path: '/marketplaces', component: () => import('@/components/www/marketplaces/index/Page.vue')},
    {path: '/partners', component: () => import('@/components/www/partners/index/Page.vue')},
    // Marketplace resources: one page per marketplace listing its field guides, and one page per
    // guided field. Fields without a guide, categories and events have no page.
    {
        path: '/marketplaces/:handle',
        component: () => import('@/components/www/marketplaces/show/Page.vue'),
        props: route => ({handle: route.params.handle as string}),
    },
    {
        path: '/marketplaces/:handle/fields/:fieldHandle',
        component: () => import('@/components/www/marketplaces/fields/show/Page.vue'),
        props: route => ({handle: route.params.handle as string, fieldHandle: route.params.fieldHandle as string}),
    },
    {
        path: '/help',
        component: () => import('@/components/www/help/index/Page.vue'),
    },
    {
        path: '/help/:category',
        component: () => import('@/components/www/help/category/Page.vue'),
        props: route => ({category: route.params.category as string}),
    },
    {
        path: '/help/:category/:handle',
        component: () => import('@/components/www/help/show/Page.vue'),
        props: route => ({category: route.params.category as string, handle: route.params.handle as string}),
    },
    {
        // Resources directory. Category subsections serve at top-level paths (/reports below)
        // even though their code stays under resources/ (see resources/shared/resources.ts).
        path: '/resources',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/resources/index/Page.vue')},
        ],
    },
    {
        // Reports category index + hand-built report pages (see resources/shared/resources.ts).
        path: '/reports',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/resources/reports/index/Page.vue')},
            {
                path: 'marketplace-fashion-brand-loyalty-report',
                component: () => import('@/components/www/resources/reports/marketplace-fashion-brand-loyalty-report/Page.vue'),
            },
            {
                path: 'cipa-fashion-brand-compliance-report',
                component: () => import('@/components/www/resources/reports/cipa-fashion-brand-compliance-report/Page.vue'),
            },
            {
                path: 'cipa-fashion-brand-compliance-report/compliance-checker',
                component: () => import('@/components/www/resources/reports/cipa-fashion-brand-compliance-report/compliance-checker/Page.vue'),
                meta: {headerTheme: 'dark'},
            },
        ],
    },
    {
        // Tools category index + one page per tool (see resources/shared/resources.ts). Top-level
        // path like /reports, though the code stays under resources/tools.
        path: '/tools',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {
                path: '',
                component: () => import('@/components/www/resources/tools/index/Page.vue'),
                meta: {preFooterCta: TOOLS_PRE_FOOTER_CTA},
            },
            {
                path: 'nordstrom-readiness-checker',
                component: () => import('@/components/www/resources/tools/nordstrom-readiness-checker/Page.vue'),
                meta: {preFooterCta: TOOLS_PRE_FOOTER_CTA},
            },
        ],
    },
    {
        // Reports used to live at /resources/reports — keep the old URLs redirecting.
        path: '/resources/reports/:rest(.*)*',
        redirect: to => {
            // rest is an array of matched segments, or '' when the bare /resources/reports matched.
            const rest = Array.isArray(to.params.rest) ? to.params.rest.join('/') : to.params.rest
            return rest ? `/reports/${rest}` : '/reports'
        },
    },
    {
        path: '/blog',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/blog/index/Page.vue')},
            {
                // Route-based category pages (crawlable + prerendered, unlike query-param filters).
                path: `:category(${BLOG_CATEGORY_ROUTE_PATTERN})`,
                component: () => import('@/components/www/blog/category/Page.vue'),
                props: route => ({category: route.params.category as string}),
            },
        ],
    },
    {
        // Canonical post route — never changes even if the category or handle do.
        // Outside the CTA layout — the post page ends with its own CTA band (BlogPostCta).
        path: '/blog/:id(o[a-z]+_[a-zA-Z0-9]+)',
        component: () => import('@/components/www/blog/show/Page.vue'),
        props: route => ({id: route.params.id as string}),
    },
    {
        path: '/blog/:category/:handle',
        component: () => import('@/components/www/blog/show/Page.vue'),
        props: route => ({
            category: route.params.category as string,
            handle: route.params.handle as string,
        }),
    },
    {
        path: '/announcements',
        component: () => import('@/components/www/layout/PreFooterCtaLayout.vue'),
        children: [
            {path: '', component: () => import('@/components/www/announcements/index/Page.vue')},
            {
                // Canonical article route — never changes even if the category or publish date do.
                path: ':id(o[a-z]+_[a-zA-Z0-9]+)',
                component: () => import('@/components/www/announcements/show/Page.vue'),
                props: route => ({id: route.params.id as string}),
            },
            {
                path: ':category/:handle/:date(\\d{4}-\\d{2}-\\d{2})',
                component: () => import('@/components/www/announcements/show/Page.vue'),
                props: route => ({
                    category: route.params.category as string,
                    handle: route.params.handle as string,
                    date: route.params.date as string,
                }),
            },
        ],
    },
    {
        path: '/change-logs',
        component: () => import('@/components/www/change-logs/index/Page.vue'),
    },
    {
        path: '/change-logs/:handle',
        component: () => import('@/components/www/change-logs/show/Page.vue'),
        props: route => ({handle: route.params.handle as string}),
    },
    {path: '/:pathMatch(.*)*', name: 'notFound', component: () => import('@/components/www/not-found/Page.vue')},
]

export default wwwRoutes
